import { Model } from 'app/model/backendmodel';
import { Response } from 'app/services/http';
import { convertStringToObject, isString, stringsAreEqualCaseInsensitive } from './string.util';
import { LoggerService } from 'app/logger/logger.service';
import { getStatusCodeByName } from './http.utils';
import { GitAuthErrorCodes } from 'app/services/git/git-models';

const invalidPrincipalIdErrorCode = 'InvalidResourceIdentityPrincipalId';
const eventTriggerLinkedErrorCode = 'LinkedAuthorizationFailed';
const authorizationFailedErrorCode = 'AuthorizationFailed';
const scopeLockedErrorCode = 'scopelocked';
const userNotLicensedErrorCode = 'UserNotLicensed';
const featureNotAvailableErrorCode = 'FeatureNotAvailable';
const fileName = 'http-response.util';

/**
 * Checks whether an error response from Fabric matches the given HTTP status and errorCode.
 * Handles multiple response shapes: custom Response (`.json()`), HttpErrorResponse (`.error`),
 * or plain objects with `.errorCode` directly.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isFabricError(errorResponse: any, expectedStatus: number, expectedErrorCode: string): boolean {
    if (!errorResponse) {
        return false;
    }
    const status = errorResponse.status ?? errorResponse.statusCode;
    if (status != null && status !== expectedStatus) {
        return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let jsonBody: any;
    if (typeof errorResponse.json === 'function') {
        try {
            jsonBody = errorResponse.json();
        } catch {
            // body may not be parseable — fall through to other candidates
        }
    }

    const candidates = [errorResponse, jsonBody, errorResponse.error, errorResponse.body, errorResponse.error?.error];
    return candidates.some((candidate) =>
        candidate && stringsAreEqualCaseInsensitive(candidate.errorCode, expectedErrorCode)
    );
}

/**
 * Detects the Fabric "User is not licensed" error (HTTP 401, errorCode: 'UserNotLicensed').
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFabricUnlicensedError(errorResponse: any): boolean {
    return isFabricError(errorResponse, 401, userNotLicensedErrorCode);
}

/**
 * Detects the Fabric "Feature not available" error (HTTP 403, errorCode: 'FeatureNotAvailable').
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFabricFeatureNotAvailableError(errorResponse: any): boolean {
    return isFabricError(errorResponse, 403, featureNotAvailableErrorCode);
}

/**
 * Detects an access error (HTTP 401 Unauthorized, 403 Forbidden, or 404 Not Found) on a Fabric
 * response. Used to decide whether the current user can access a mounted data factory before
 * opening it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFabricAccessError(errorResponse: any): boolean {
    if (!errorResponse) {
        return false;
    }
    const status = errorResponse.status ?? errorResponse.statusCode;
    return status === 401 || status === 403 || status === 404;
}

export interface IErrorResponseDetails {
    httpStatus: number;
    httpStatusText: string;
    innerError: string;
    message: string;
    target?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isResourceLockedErrorResponse(errorResponse: any, services: { logger: LoggerService }): boolean {
    const errors = getDeploymentErrorsFromResponse(errorResponse, services);
    return errors.some((error) => {
        return isResourceLockedError(error);
    });
}

export function isResourceLockedError(error: IErrorResponseDetails): boolean {
    return (
        stringsAreEqualCaseInsensitive(error.httpStatusText, scopeLockedErrorCode) ||
        stringsAreEqualCaseInsensitive(error.innerError, scopeLockedErrorCode)
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isInvalidPrincipalIdError(error: any, services: { logger: LoggerService }): boolean {
    return getDeploymentErrorsFromResponse(error, services).some((error) => error.innerError === invalidPrincipalIdErrorCode);
}

export function isGitPermissionsError(errorResponse: Response): boolean {
    const respMessage = extractErrorMessageFromRPResponse(errorResponse);
    for (const errorCode in GitAuthErrorCodes) {
        if (respMessage.startsWith(errorCode)) {
            return true;
        }
    }
    return false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isUnableToListOperationStatus(errorResponse: any): boolean {
    const error = tryExtractErrorResponseFromRP(errorResponse);
    return (
        error &&
        stringsAreEqualCaseInsensitive(error.code, authorizationFailedErrorCode) &&
        `${error.message}`.includes(`Microsoft.Resources/deployments/operationStatuses/read`)
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isUnableToDeployInRg(errorResponse: any): boolean {
    const error = tryExtractErrorResponseFromRP(errorResponse);
    return (
        error &&
        stringsAreEqualCaseInsensitive(error.code, authorizationFailedErrorCode) &&
        `${error.message}`.includes(`Microsoft.Resources/deployments/write`)
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function containsEventGridAuthorizationError(errorResponse: any, services: { logger: LoggerService }): boolean {
    const errors = getDeploymentErrorsFromResponse(errorResponse, services);
    return errors.some((error) => {
        return isUnableToSubscribeToEvents(error);
    });
}

export function isUnableToSubscribeToEvents(error: IErrorResponseDetails): boolean {
    return (
        error &&
        stringsAreEqualCaseInsensitive(error.innerError, eventTriggerLinkedErrorCode) &&
        `${error.message}`.includes(`Microsoft.EventGrid/EventSubscriptions/Write`)
    );
}

export function isPbiErrorResponse(
    responseError: Response | Model.ErrorResponse | Model.PbiErrorResponse
): responseError is Model.PbiErrorResponse {
    return (responseError as Model.PbiErrorResponse)?.error?.['pbi.error'] != null;
}

export function getDeploymentErrorsFromResponse(
    responseError: Response | Model.ErrorResponse | Model.PbiErrorResponse, services: { logger: LoggerService }
): IErrorResponseDetails[] {
    if (!responseError) {
        return [];
    }
    if (responseError instanceof Response) {
        return [extractErrorFromResponse(responseError)];
    } else if (!isPbiErrorResponse(responseError) && responseError.details?.length > 0) {
        return extractErrorDetailsFromResponse(responseError);
    } else if (!isPbiErrorResponse(responseError) && responseError.message && responseError.code) {
        return [
            createErrorResponseDetail(
                getStatusCodeByName(responseError.code),
                responseError.code,
                responseError.code,
                responseError.message,
                responseError.target
            )
        ];
    } else if (isPbiErrorResponse(responseError)) {
        return extractPbiErrorFromResponse(responseError);
    } else {
        services.logger.log(fileName, 'Unable to get error details of responseError object');
    }
    return [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractErrorMessageFromRPResponse(error: any): string {
    const response = tryExtractErrorResponseFromRP(error);
    if (isString(response.message)) {
        return response.message;
    }
    let messageStr: string;
    try {
        messageStr = JSON.stringify(error, Object.getOwnPropertyNames(error));
    } catch {
        try {
            messageStr = JSON.stringify(error);
        } catch {
            messageStr = '' + error;
        }
    }
    return messageStr;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function tryExtractErrorResponseFromRP(error: any): Model.ErrorResponse {
    if (error && error instanceof Response) {
        try {
            const response = error.json();
            if (response?.error) {
                const errorResponse = response.error as Model.ErrorResponse;
                if (errorResponse != null && errorResponse.code != null && errorResponse.message != null) {
                    return errorResponse;
                }
                // for different older api version error format is different, Message is for Synapse
            } else if (response && (response.message || response.Message)) {
                response.message = response.message || response.Message;
                return response;
            } else if (response?.Error) {
                // XML error response has the same structure but in PascalCase
                if (response.Error.Code != null && response.Error.Message != null) {
                    return {
                        code: response.Error.Code,
                        message: response.Error.Message
                    };
                }
            }
        } catch {
            // Do nothing
        }
    }
    return error;
}

function extractPbiErrorFromResponse(responseError: Model.PbiErrorResponse): IErrorResponseDetails[] {
    const requestId = responseError.headers?.requestid;
    const stringPrefix =
        (responseError.error?.['pbi.error'].details?.[0]?.detail?.value ?? '') + (responseError.error?.code ?? '');
    const errorMsg = stringPrefix
        ? `${stringPrefix}, ${stringResources.RequestId.toLocaleLowerCase()}: ${requestId}.`
        : `${stringResources.RequestId}: ${requestId}.`;
    return [
        createErrorResponseDetail(
            getStatusCodeByName(responseError.statusText),
            responseError.statusText,
            errorMsg,
            errorMsg
        )
    ];
}

function extractErrorFromResponse(responseError: Response): IErrorResponseDetails {
    const armError = tryExtractErrorResponseFromRP(responseError);
    // We try first to get the ARM error code because publish errors are grouped in the response body and sometimes this response returns with a 200 status code.
    const statusCode =
        getStatusCodeByName(armError.code) > 0 ? getStatusCodeByName(armError.code) : responseError.status;
    return createErrorResponseDetail(
        statusCode,
        responseError.statusText,
        armError.code,
        armError.message,
        armError.target
    );
}

function extractErrorDetailsFromResponse(responseError: Model.ErrorResponse): IErrorResponseDetails[] {
    const responseDetailErrors: IErrorResponseDetails[] = [];
    responseError.details?.forEach((detail) => {
        const armError = extractErrorObjectInString(detail.message);
        const errorResponseDetail = createErrorResponseDetail(
            getStatusCodeByName(detail.code),
            detail.code,
            armError.code,
            armError.message,
            armError.target
        );
        responseDetailErrors.push(errorResponseDetail);
    });
    return responseDetailErrors;
}

function createErrorResponseDetail(
    httpStatus: number,
    httpStatusText: string,
    innerError: string,
    message: string,
    target?: string
): IErrorResponseDetails {
    return {
        httpStatus,
        httpStatusText,
        innerError,
        message,
        target
    };
}

function extractErrorObjectInString(error: string): Model.ErrorResponse {
    let errorObject: Model.ErrorResponse;
    const jsonError = convertStringToObject(error);
    // This is GA Response
    if (jsonError?.error) {
        errorObject = {
            code: jsonError.error.code || '',
            message: jsonError.error.message || '',
            target: jsonError.error.target
        };
    } else if (jsonError) {
        // This is 09-01-Preview Response
        errorObject = {
            code: jsonError.code || '',
            message: jsonError.message || '',
            target: jsonError.target
        };
    } else {
        // We should not reach this case, this means that the JSON is invalid.
        errorObject = {
            code: '',
            message: error
        };
    }
    return errorObject;
}
